import QS from "qs";
import { jsonClient } from "@/lib/raftApi";
import { useDrupalSettings } from "@/composables/useDrupalSettings";
import jsonapiClient from "./jsonapi_client";
import PathMapper from "./PathMapper";
import raftRestClient from "./raft_rest_client";

const { config } = useDrupalSettings();

const uuidPattern =
  "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";

const machineNamePattern = "[a-z0-9_]+";

export { uuidPattern, machineNamePattern };

const raftGenericHelper = {
  capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  },

  formatCreatedByUserName: function (userObject) {
    if (Number.parseInt(userObject.drupal_internal__uid, 10) === 1) {
      return "Admin";
    }
    return userObject.field_prof_display_name;
  },

  /**
   * Format a string from a Date object.
   *
   * @todo Replace with Luxon.toLocaleString().
   * @see https://moment.github.io/luxon/docs/manual/formatting
   *
   * @param {Date} value
   *   The date value.
   * @param {string} delimiter
   *   The delimiter between date parts.
   *
   * @return {string}
   *   Formatted date.
   */
  formatDateString(value, delimiter = "-") {
    if (!value) {
      return "--";
    }
    const date = new Date(value);
    return (
      date.getMonth() +
      1 +
      delimiter +
      date.getDate() +
      delimiter +
      date.getFullYear()
    );
  },

  /**
   * Format a date as a string in YYYY-MM-DD format.
   *
   * @todo Replace with Luxon.toISODate().
   * @see https://moment.github.io/luxon/docs/manual/formatting
   *
   * @param {Date} date
   *   Object representing a javascript Date.
   *
   * @return {string}
   *   Returns a string representing a date in YYYY-MM-DD format.
   */
  formatDateFilterYyyyMmDd(date) {
    if (!(date instanceof Date)) {
      date = new Date(date);
    }
    const dd = (date.getDate() < 10 ? "0" : "") + date.getDate();

    const mm = (date.getMonth() + 1 < 10 ? "0" : "") + (date.getMonth() + 1);

    return date.getFullYear() + "-" + mm + "-" + dd;
  },

  /**
   * Format a datetime as a string.
   *
   * @param {Date} date
   *   Datetime object.
   *
   * @return {string}
   *   Returns a formatted string as YYYY-MM-DD HH:ii:ss am/pm format.
   */
  formatDateTime(date) {
    const dateString = this.formatDateFilterYyyyMmDd(date);
    return dateString + " " + date.toLocaleTimeString();
  },

  /**
   * Convert a Date object to a unix timestamp.
   *
   * @todo Replace with Luxon.toSeconds().
   * @see https://moment.github.io/luxon/docs/manual/formatting
   *
   * @param {Object|Date} value
   *   Object representing a date value.
   * @param {Boolean} overrideTime
   *   Flag to control whether time of day in the date object should be overridden.
   * @param {Number} hour
   *   Hour to set if overriding time.
   * @param {Number} minute
   *   Minute to set if overriding time.
   * @param {Number} second
   *   Second to set if overriding time.
   *
   * @return {number}
   *   Returns an integer representing a Unix timestamp.
   */
  datepickerValueToUnixTimestamp: function (
    value,
    overrideTime = false,
    hour = 0,
    minute = 0,
    second = 0,
  ) {
    if (value && typeof value === "object") {
      // Convert the afterDate to a full string so we can set the
      // hours, minutes and seconds to what we want and not let them
      // default to the current time.
      const newDate = new Date(
        value.getFullYear(),
        value.getMonth(),
        value.getDate(),
        value.getHours(),
        value.getMinutes(),
        value.getSeconds(),
      );
      if (overrideTime) {
        newDate.setHours(hour);
        newDate.setMinutes(minute);
        newDate.setSeconds(second);
      }
      // Now that we have our date with the proper time, convert it to a proper timestamp.
      return Math.floor(newDate.getTime() / 1000);
    }
  },

  /**
   * Convert a Date object to an iso date format.
   *
   * @todo Replace with Luxon.toISO().
   *
   * @param value
   * @return {string}
   */
  datepickerValueToIso: function (value) {
    value = new Date(value);
    // Remove trailing milliseconds and Z like ".000Z".
    return value.toISOString().slice(0, -5) + "Z";
  },
  getDatepickerPlaceholder: function () {
    const currentDate = new Date();
    // Return an iso8601 formatted date in the current time zone.
    return (
      currentDate.getFullYear() +
      "-" +
      ("0" + (currentDate.getMonth() + 1)).slice(-2) +
      "-" +
      ("0" + currentDate.getDate()).slice(-2)
    );
  },
  getDatepickerHighlighted: function () {
    return { dates: [new Date()] };
  },
  toggleBusy(ctx) {
    ctx.isBusy = !ctx.isBusy;
    this.tableResetScroll(ctx);
    ctx.$refs.table.refresh();
  },
  tableResetScroll(ctx) {
    ctx.$refs.table.$el.scrollTo(0, 0);
  },
  async getUserUuid(userId) {
    const options = {
      path: "user/user",
      queries: {
        "filter[drupal_internal__uid]": userId,
        "fields[user--user]": "id",
      },
    };
    const userData = await jsonClient
      .get(options)
      .catch((exception) => console.error(exception));
    return userData.data[0].id;
  },
  getUserDisplayNameFromUidJsonAPI(userId, callBack) {
    if (!userId) {
      return;
    }
    const filterString =
      "filter[d][path]=drupal_internal__uid&filter[d][value]=" + userId;
    jsonapiClient.getJSONapi(
      "user/user",
      filterString,
      (response) => {
        if (response.meta.count !== 1) {
          return;
        }
        callBack.call(
          this,
          response.data[0].attributes.field_prof_display_name,
        );
      },
      this,
      true,
    );
  },
  displayingCurrent(currentPage, itemsPerPage = 25) {
    if (currentPage > 1) {
      return (currentPage - 1) * itemsPerPage + 1;
    }
    return 1;
  },
  displayingCurrentTotal(
    totalItems,
    totalPages,
    currentPage,
    itemsPerPage = 25,
  ) {
    if (totalItems < itemsPerPage) {
      return totalItems;
    }
    if (totalPages === currentPage) {
      return totalItems;
    }
    if (currentPage * itemsPerPage > totalItems) {
      return totalItems;
    }
    return this.displayingCurrent(currentPage, itemsPerPage) + itemsPerPage - 1;
  },
  getCsvIconPath(isB4) {
    return PathMapper.getIconAssetPath("CSV.svg", isB4);
  },
  getHelpIconPath(isB4) {
    return PathMapper.getIconAssetPath("help.png", isB4);
  },
  getDownloadIconPath(isB4) {
    return PathMapper.getIconAssetPath("download.png", isB4);
  },
  getEmailIconPath(isB4) {
    return PathMapper.getIconAssetPath("Message.svg", isB4);
  },
  updateBrowserHistory(filter) {
    // Set the browser history
    if (window.history.pushState) {
      const newurl =
        window.location.protocol +
        "//" +
        window.location.host +
        window.location.pathname +
        "?" +
        QS.stringify(filter);
      window.history.pushState({ path: newurl }, "", newurl);
    }
  },
  /**
   * Extract a param from the query string.
   *
   * @param {Array} keys - Strings representing parameters in the query string.
   *
   * @return {Object} - Returns the values from the query string, or null if not found.
   */
  getFilterFromUrl(keys) {
    const uri = window.location.search.substring(1);
    const params = new QS.parse(uri);
    const filter = {};
    for (const key of keys) {
      // Use of undefined here is deliberate, since this means it's skipped by QS.stringify().
      filter[key] = params[key];
    }
    return filter;
  },
  fetchUserPermissions(permissionList, groupContext) {
    const userPerms = [];

    const queries = {};
    queries.permissions = permissionList;
    if (groupContext) {
      queries["rog-type"] = "node";
      queries["rog-id"] = groupContext;
    }
    raftRestClient.getRest(
      "/perms",
      queries,
      (data) => {
        for (const [key, value] of Object.entries(data.permissions)) {
          if (value) {
            userPerms.push(key);
          }
        }
      },
      null,
      this,
      true,
    );
    return userPerms;
  },
  validateUrl: function (value) {
    return /^(?:(?:(?:http[s]?):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(
      value,
    );
  },
  formatBytes: function (bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / k ** i).toFixed(dm)) + " " + sizes[i];
  },
  /**
   * Adjust a URL depending on whether we're seeing it in the context of B3 or B4.
   *
   * @param {string} url
   *   The URL to adjust.
   * @param {boolean} addGroupContext
   *   True to add current group context if any.
   * @param {boolean} addB4Base
   *   True to prepend B4 base URL if on B3.
   * @param {boolean} addFileJwt
   *   True to add file JWT if on B3.
   *
   * @return {string}
   *   The modified URL.
   */
  adjustFileURL(
    url,
    addGroupContext = true,
    addB4Base = true,
    addFileJwt = true,
  ) {
    const params = new URLSearchParams("");
    if (url && !config.isB4) {
      if (addFileJwt) {
        params.set("jwt", config.fileJwt);
      }
      if (addB4Base) {
        url = config.baseUrl + url;
      }
    }
    if (addGroupContext && config.groupContext) {
      params.set("rog-type", "node");
      params.set("rog-id", config.groupContext);
    }
    const queryString = params.toString();
    if (queryString) {
      url += "?" + queryString;
    }
    return url;
  },

  /**
   * Sets the page title and H1 whether in B3 or B4.
   *
   * @param {string} title
   *   The new title to set.
   * @param {boolean} setTabTitle
   *   True to also set the window/tab title.
   *
   * @return {Element}
   *   The title element.
   */
  setPageTitle(title, setTabTitle = true) {
    let titleElement = document.querySelector("#page-title h1");
    if (titleElement) {
      titleElement.textContent = title;
    } else {
      titleElement = document.querySelector("h1.page-title");
      if (titleElement) {
        titleElement.textContent = title;
      }
    }
    if (setTabTitle) {
      document.title = title;
    }
    return titleElement;
  },

  /**
   * Check whether a file exists and is able to be accessed by the client.
   *
   * @param {string} filePath
   *   The path to the file.
   * @param {Function} callback
   *   Callback to handle response.
   * @param {Function} errorCallback
   *   Callback to handle Error.
   * @param {Object} context
   *   Context object.
   * @param {string} baseUrl
   *   Base path to initialize axios.
   */
  checkFileExists: function (
    filePath,
    callback,
    errorCallback,
    context,
    baseUrl = "/",
  ) {
    if (filePath) {
      raftRestClient.headRest(
        filePath,
        null,
        (response) => {
          callback.call(context, response);
        },
        (error) => {
          errorCallback.call(context, error);
        },
        context,
        baseUrl,
      );
    }
  },

  /**
   * Clone an object.
   *
   * @param {Object} object
   *   An object to clone.
   *
   * @return {Object}
   *   Returns an object that was cloned.
   */
  cloneObject: function (object) {
    return JSON.parse(JSON.stringify(object));
  },

  /**
   * Checks if an object is empty or not.
   *
   * @param {Object} object
   *   An object to clone.
   *
   * @return {Boolean}
   *   True if object is not empty, false otherwise.
   */
  isNotEmpty: function (object) {
    // Handles object and array.
    if (object && typeof object === "object") {
      return Object.keys(object).length > 0;
    }
    if (object === false) {
      return true;
    }
    return !!object;
  },

  /**
   * Get the path to the PDF viewer.
   *
   * @param {string} filePath
   *   The path to the PDF file to view.
   *
   * @return {string}
   *   Path
   */
  viewerjsPath: function (filePath) {
    let base = "";
    if (!config.isB4) {
      base = Drupal.settings.bioraft.b4_base_url;
    }
    return base + "/libraries/viewerjs/ViewerJS/index.html#" + filePath;
  },

  /**
   * Trims a filename to a maximum length with an ellipsis before the extension.
   *
   * @param {string} filename
   *   The filename.
   * @param {number} maxlen
   *   The maximum length.
   *
   * @return {string}
   *   The trimmed filename.
   */
  trimFilename(filename, maxlen) {
    if (!filename) {
      return "";
    }
    if (filename.length <= maxlen) {
      return filename;
    }
    const ext = filename.split(".").pop();
    const trimmed = filename.substr(0, filename.length - ext.length);
    return trimmed.substr(0, maxlen - (3 + ext.length)) + "..." + ext;
  },

  /**
   * Trims plain text to a maximum length with a trailing ellipsis.
   *
   * @param {string} text
   *   The text.
   * @param {number} maxlen
   *   The maximum length.
   *
   * @return {string}
   *   The trimmed text.
   */
  trimText(text, maxlen) {
    if (text.length > maxlen) {
      return text.substr(0, maxlen - 3) + "...";
    }
    return text;
  },

  fetchNodeTitles(nodeIds) {
    const queries = {};
    const nodeTitles = [];
    queries.nids = nodeIds;
    raftRestClient.getRest(
      "/node/titles",
      queries,
      (data) => {
        data.nodes.forEach((nodeData) => {
          nodeTitles.push(nodeData);
        });
      },
      null,
      this,
      true,
    );
    return nodeTitles;
  },

  getDestinationQuery(encodeQueryString = true) {
    let dest = "destination=" + window.location.pathname;
    // Handle query strings, especially gids[] in B3.
    // Allow bypass of encoding the query string for cases where we know it's
    // already been encoded, and double-encoding will break things.  This is
    // relevant when pulling the gids[] while in the context of the setup wizard
    // in B3, where they are already encoded.
    if (window.location.search) {
      if (encodeQueryString) {
        dest += encodeURIComponent(window.location.search);
      } else {
        dest += window.location.search;
      }
    }
    return dest;
  },

  /**
   * Checks if a given date is marked disabled or not.
   *
   * @param {Date} date
   *   A javascript Date() object to check.
   * @param {Object} disabledDates
   *   A disabled date as defined in the vuejs-datepicker.
   *   https://github.com/charliekassel/vuejs-datepicker#disabled-dates
   *
   * @return {boolean}
   *   True if the date is not marked disabled, false otherwise.
   */
  isDateEnabled(date, disabledDates) {
    if (typeof date === "undefined" || date === null) {
      return true;
    }
    let disabledDate = true;
    if (typeof disabledDates === "undefined") {
      return true;
    }
    if (typeof disabledDates.to !== "undefined" && disabledDates.to) {
      if (
        (date.getMonth() < disabledDates.to.getMonth() &&
          date.getFullYear() <= disabledDates.to.getFullYear()) ||
        date.getFullYear() < disabledDates.to.getFullYear()
      ) {
        disabledDate = false;
      }
    }
    if (typeof disabledDates.from !== "undefined" && disabledDates.from) {
      if (
        (date.getMonth() > disabledDates.from.getMonth() &&
          date.getFullYear(date) >= disabledDates.from.getFullYear()) ||
        date.getFullYear() > disabledDates.from.getFullYear()
      ) {
        disabledDate = false;
      }
    }
    if (typeof disabledDates.dates !== "undefined") {
      disabledDates.dates.forEach((d) => {
        if (this.compareDates(date, d)) {
          disabledDate = false;
        }
      });
    }
    if (
      typeof disabledDates.to !== "undefined" &&
      disabledDates.to &&
      date < disabledDates.to
    ) {
      disabledDate = false;
    }
    if (
      typeof disabledDates.from !== "undefined" &&
      disabledDates.from &&
      date >= disabledDates.from
    ) {
      disabledDate = false;
    }
    return disabledDate;
  },

  compareDates(date1, date2, useUtc = false) {
    const d1 = new Date(date1.getTime());
    const d2 = new Date(date2.getTime());

    if (useUtc) {
      d1.setUTCHours(0, 0, 0, 0);
      d2.setUTCHours(0, 0, 0, 0);
    } else {
      d1.setHours(0, 0, 0, 0);
      d2.setHours(0, 0, 0, 0);
    }
    return d1.getTime() === d2.getTime();
  },

  /**
   * Increment number or the last number in a string for id.
   *
   * Examples:
   * 123 -> "124"
   * "1234" -> "1235"
   * "1239" -> "1240"
   * "A-01-1234-9-C" -> "A-01-1234-10-C"
   * "A-01-1234-01-C" -> "A-01-1234-02-C"
   * "fish" -> "fish1"
   * "-12" -> "-13"
   * -12 -> '-13'
   * null -> '1'
   * undefined -> '1'
   * NaN -> '1'
   *
   * @see incrementLastNumber in B3 chemtracker.js
   *
   * @param {string|number} input
   *   The input string or number to adjust.
   * @return {string}
   *   The adjusted string.
   */
  incrementLastNumber(input) {
    let inputString = "";
    let inputNumberString = "";
    let inputNumber = 0;

    if (typeof input === "number") {
      if (!Number.isNaN(input)) {
        inputNumber = Math.abs(input);
        inputString = input + "";
        inputNumberString = inputNumber + "";
      }
    } else if (typeof input === "string") {
      inputString = input;
      const matches = input.match(/\d+/g);
      if (matches !== null) {
        inputNumberString = matches.slice(-1).pop();
      }
      inputNumber = +inputNumberString;
    }

    const outputNumber = inputNumber + 1;
    let outputNumberString = outputNumber + "";
    if (outputNumberString.length < inputNumberString.length) {
      const padding = new Array(
        inputNumberString.length - outputNumberString.length + 1,
      ).join("0");
      outputNumberString = (padding + outputNumber).slice(
        -1 * inputNumberString.length,
      );
    }

    const pos = inputString.lastIndexOf(inputNumberString);
    const head = inputString.substring(0, pos);
    const tail = inputString.substring(pos + inputNumberString.length);

    return head + outputNumberString + tail;
  },
};

export default raftGenericHelper;
